import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Alert, Button, Spinner } from 'react-bootstrap'
import { pink } from '@material-ui/core/colors'
import { useLocation } from 'react-router-dom'
import useAuth from 'hooks/useAuth'
import {
  REQUIRE_FIELD_CONFIG_CODE,
  REQUIRE_OPTION_CONFIG_CODE,
  resolveRequireConfigIdentity,
  resolveRequiredValue,
} from './config'
import { loadRequireConfig, saveRequireOptionConfig } from './service'

const MISSING_IDENTITY_MESSAGE =
  'ไม่สามารถเปิดการตั้งค่า Require ได้ หน้านี้ยังไม่ได้กำหนด requireConfigKey กรุณาติดต่อ Developer'

const RequireConfigContext = createContext(null)
const RequireConfigBarHostContext = createContext(null)
const REQUIRE_CONFIG_BAR_STICKY_STYLE = {
  bottom: 0,
  position: 'sticky',
  zIndex: 1020,
}
const REQUIRE_TOGGLE_CHECK_ICON =
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' ` +
  `viewBox='0 0 16 16'%3E%3Cpath d='M3 8l3 3 6-7' fill='none' ` +
  `stroke='white' stroke-width='2' stroke-linecap='round' ` +
  `stroke-linejoin='round'/%3E%3C/svg%3E")`

const replaceAccountConfig = (config = [], nextConfig) => [
  ...config.filter(({ configCode }) => configCode !== nextConfig.configCode),
  nextConfig,
]

export const RequireConfigProvider = ({
  children,
  scopeKey,
  aliases = [],
  formCode,
  canConfigure: allowConfigure = true,
}) => {
  const location = useLocation()
  const auth = useAuth()
  const isAdmin = auth?.isAdmin === true
  const canConfigure = allowConfigure && isAdmin
  const accountConfig = useMemo(() => auth?.config || [], [auth?.config])
  const accountConfigRef = useRef(accountConfig)
  const identity = useMemo(
    () => resolveRequireConfigIdentity({ scopeKey, aliases, formCode }),
    [scopeKey, aliases, formCode],
  )
  const isRequireMode = isAdmin && ['1', 'true'].includes(
    new URLSearchParams(location.search || '').get('isRequire'),
  )
  const [optionConfig, setOptionConfig] = useState(() =>
    accountConfig.find(
      ({ configCode }) => configCode === REQUIRE_OPTION_CONFIG_CODE,
    ),
  )
  const [requireFieldConfig, setRequireFieldConfig] = useState(() =>
    accountConfig.find(
      ({ configCode }) => configCode === REQUIRE_FIELD_CONFIG_CODE,
    ),
  )
  const [pending, setPending] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [reloadToken, setReloadToken] = useState(0)
  const warningKeyRef = useRef('')
  const registryRef = useRef(new Map())

  useEffect(() => {
    accountConfigRef.current = accountConfig
  }, [accountConfig])

  useEffect(() => {
    setPending({})
  }, [identity.scopeKey])

  useEffect(() => {
    let active = true
    setIsLoading(true)
    setError('')
    setMessage('')

    Promise.all([
      loadRequireConfig(REQUIRE_OPTION_CONFIG_CODE),
      loadRequireConfig(REQUIRE_FIELD_CONFIG_CODE),
    ])
      .then(([nextOptionConfig, nextRequireFieldConfig]) => {
        if (!active) return
        setOptionConfig(nextOptionConfig || { jsonData: {} })
        setRequireFieldConfig(nextRequireFieldConfig || { jsonData: {} })
      })
      .catch((loadError) => {
        if (!active) return
        setError(
          loadError?.response?.data?.message ||
            loadError?.message ||
            'ไม่สามารถโหลดการตั้งค่า Require ได้',
        )
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })

    return () => {
      active = false
    }
  }, [reloadToken])

  useEffect(() => {
    if (!isRequireMode || identity.scopeKey) return
    const warningKey = `${location.pathname}${location.search}`
    if (warningKeyRef.current === warningKey) return
    warningKeyRef.current = warningKey
    console.warn(
      `[RequireConfig] ${warningKey}: add requireConfigKey/scopeKey through page metadata or RequireConfigProvider props.`,
    )
  }, [identity.scopeKey, isRequireMode, location.pathname, location.search])

  const resolveFieldRequired = useCallback(
    (fieldKey, systemRequired = false, fieldAliases = []) =>
      resolveRequiredValue({
        fieldKey,
        fieldAliases,
        scopeKey: identity.scopeKey,
        aliases: identity.aliases,
        pending,
        optionConfig,
        requireFieldConfig,
        systemRequired,
      }).value,
    [identity, optionConfig, pending, requireFieldConfig],
  )

  const setFieldRequired = useCallback((fieldKey, required) => {
    setMessage('')
    setPending((current) => ({ ...current, [fieldKey]: Boolean(required) }))
  }, [])

  const registerField = useCallback((registrationId, field) => {
    registryRef.current.set(registrationId, field)
    return () => registryRef.current.delete(registrationId)
  }, [])

  const save = useCallback(async () => {
    if (
      !identity.scopeKey ||
      !canConfigure ||
      !isRequireMode ||
      isSaving ||
      Object.keys(pending).length === 0
    ) {
      return false
    }

    setIsSaving(true)
    setError('')
    try {
      const nextConfig = await saveRequireOptionConfig({
        scopeKey: identity.scopeKey,
        pending,
      })
      setOptionConfig(nextConfig)
      auth?.setAccountConfig?.(
        replaceAccountConfig(accountConfigRef.current, nextConfig),
      )
      setPending({})
      setMessage('บันทึกการตั้งค่า Require สำเร็จ')
      return true
    } catch (saveError) {
      setError(
        saveError?.response?.data?.message ||
          saveError?.message ||
          'ไม่สามารถบันทึกการตั้งค่า Require ได้',
      )
      return false
    } finally {
      setIsSaving(false)
    }
  }, [auth, canConfigure, identity.scopeKey, isRequireMode, isSaving, pending])

  const value = useMemo(
    () => ({
      aliases: identity.aliases,
      canConfigure,
      cancel: () => {
        setPending({})
        setMessage('')
      },
      error,
      isDirty: Object.keys(pending).length > 0,
      isLoading,
      isRequireMode,
      isSaving,
      message,
      pending,
      registerField,
      reload: () => setReloadToken((current) => current + 1),
      resolveFieldRequired,
      save,
      scopeKey: identity.scopeKey,
      setFieldRequired,
    }),
    [
      canConfigure,
      error,
      identity,
      isLoading,
      isRequireMode,
      isSaving,
      message,
      pending,
      registerField,
      resolveFieldRequired,
      save,
      setFieldRequired,
    ],
  )

  return (
    <RequireConfigContext.Provider value={value}>
      {children}
    </RequireConfigContext.Provider>
  )
}

export const useRequireConfig = () => {
  const context = useContext(RequireConfigContext)
  if (context) return context

  return {
    aliases: [],
    canConfigure: false,
    cancel: () => {},
    error: '',
    isDirty: false,
    isLoading: false,
    isRequireMode: false,
    isSaving: false,
    message: '',
    pending: {},
    registerField: () => () => {},
    reload: () => {},
    resolveFieldRequired: (fieldKey, systemRequired = false) =>
      Boolean(systemRequired),
    save: async () => false,
    scopeKey: '',
    setFieldRequired: () => {},
  }
}

export const useRequireConfigField = ({
  fieldKey,
  fieldAliases = [],
  label = '',
  type = '',
  value,
  visible = true,
  disabled = false,
  readOnly = false,
  configurable = true,
  systemRequired = false,
}) => {
  const context = useRequireConfig()
  const { registerField } = context
  const registrationId = useRef(
    `${fieldKey || 'field'}:${Math.random().toString(36).slice(2)}`,
  )
  const required = context.resolveFieldRequired(
    fieldKey,
    systemRequired,
    fieldAliases,
  )

  useEffect(
    () =>
      registerField(registrationId.current, {
        configurable,
        disabled,
        fieldAliases,
        fieldKey,
        label,
        readOnly,
        required,
        type,
        value,
        visible,
      }),
    [
      configurable,
      registerField,
      disabled,
      fieldAliases,
      fieldKey,
      label,
      readOnly,
      required,
      type,
      value,
      visible,
    ],
  )

  return {
    ...context,
    configurable,
    fieldKey,
    label,
    required,
    setRequired: (nextRequired) =>
      context.setFieldRequired(fieldKey, nextRequired),
    showToggle:
      context.isRequireMode &&
      context.canConfigure &&
      Boolean(context.scopeKey) &&
      configurable &&
      visible &&
      !disabled &&
      !readOnly,
  }
}

export const RequireToggle = ({ field }) => {
  if (!field?.showToggle) return null

  const checked = Boolean(field.required)
  const disabled = field.isLoading || field.isSaving || Boolean(field.error)

  return (
    <input
      type="checkbox"
      checked={checked}
      title={`บังคับกรอก ${field.label || field.fieldKey || ''}`}
      aria-label={`บังคับกรอก ${field.label || field.fieldKey || ''}`}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => field.setRequired(event.target.checked)}
      disabled={disabled}
      style={{
        appearance: 'none',
        backgroundColor: checked ? pink[500] : 'transparent',
        backgroundImage: checked ? REQUIRE_TOGGLE_CHECK_ICON : 'none',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '14px 14px',
        border: `2px solid ${pink[500]}`,
        borderRadius: '2px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        flex: '0 0 auto',
        height: '18px',
        margin: 0,
        opacity: disabled ? 0.6 : 1,
        outlineOffset: '2px',
        WebkitAppearance: 'none',
        width: '18px',
      }}
      className="mr-2"
    />
  )
}

export const RequireLabel = ({ children, required = false }) => (
  <>
    {children}
    {required ? (
      <span
        className="text-danger ml-1"
        style={{ color: '#dc3545' }}
        aria-hidden="true"
      >
        *
      </span>
    ) : null}
  </>
)

export const useRequireConfigBarHost = () =>
  useContext(RequireConfigBarHostContext)

export const RequireConfigBarHostProvider = ({ children }) => {
  const [host, setHost] = useState(null)

  return (
    <>
      <RequireConfigBarHostContext.Provider value={host}>
        {children}
      </RequireConfigBarHostContext.Provider>
      <div ref={setHost} style={REQUIRE_CONFIG_BAR_STICKY_STYLE} />
    </>
  )
}

export const RequireConfigBar = ({ title = 'ตั้งค่า Require' }) => {
  const context = useRequireConfig()
  if (!context.isRequireMode) return null
  if (!context.canConfigure) return null

  if (!context.scopeKey) {
    return (
      <Alert variant="danger" role="alert" className="mt-3">
        {MISSING_IDENTITY_MESSAGE}
      </Alert>
    )
  }

  return (
    <div
      className="d-flex flex-wrap align-items-center justify-content-between mt-3 p-3 bg-white border rounded"
      role="region"
      aria-label={title}
      style={REQUIRE_CONFIG_BAR_STICKY_STYLE}
    >
      <div className="mr-3">
        <strong className="d-block">{title}</strong>
        <span className="text-muted">
          เลือกช่องที่ต้องบังคับกรอก แล้วกดบันทึกการตั้งค่า
        </span>
        {context.error ? (
          <div className="text-danger mt-1" role="alert">
            {context.error}
          </div>
        ) : null}
        {context.message ? (
          <div className="text-success mt-1" role="status">
            {context.message}
          </div>
        ) : null}
      </div>
      <div className="d-flex align-items-center mt-2 mt-md-0">
        {context.error && context.isLoading === false ? (
          <Button
            size="sm"
            variant="outline-danger"
            className="mr-2"
            onClick={context.reload}
            disabled={context.isSaving}
          >
            ลองใหม่
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="outline-secondary"
          className="mr-2"
          onClick={context.cancel}
          disabled={!context.isDirty || context.isSaving}
        >
          ยกเลิก
        </Button>
        <Button
          size="sm"
          variant="primary"
          onClick={context.save}
          disabled={
            !context.isDirty ||
            context.isLoading ||
            context.isSaving ||
            Boolean(context.error)
          }
        >
          {context.isSaving ? (
            <Spinner animation="border" size="sm" className="mr-1" />
          ) : null}
          บันทึกการตั้งค่า
        </Button>
      </div>
    </div>
  )
}

export { MISSING_IDENTITY_MESSAGE }
